import { CONFIG } from "../config";
import initModules from "../initModules";

class BfvFilteredLazyload {
  constructor(options = {}) {
    this.$root = options.element;
    this.$spinner = this.$root.querySelector(".js-filtered-lazyload-spinner");
    this.$contentWrapper = this.$root.querySelector(
      ".js-filtered-lazyload-content-wrapper",
    );
    this.$dataContainer = this.$root.querySelector(
      ".js-filtered-lazyload-data-container",
    );
    this.$tagSection = this.$root.querySelector(
      ".bfv-filtered-lazyload__selected-filters",
    );
    this.$tagWrapper = this.$root.querySelector(
      ".js-lazyload-filter-tag-wrapper",
    );
    [this.$tagSkeleton] = this.$root.querySelectorAll(
      ".js-lazyload-filter-tag",
    );
    this.$noResults = this.$root.querySelector(".js-lazyload-no-content");
    this.$filterResult = false;
    this.$filterAndLoadMore = false;
    this.$loadMore = false;
    this.$filterChanged = false;
    this.$resetFilter = false;

    this.setFilters();
    this.toggleSpinner(false);
  }

  init() {
    this.bindEvents();
  }

  bindEvents() {
    this.$filters.forEach(($filter) => {
      if (!$filter.hasFilterListener) {
        if ($filter.dataset.filterType === "datepicker") {
          $filter.addEventListener(CONFIG.events.BFV_DATEPICKER_CHANGED, () => {
            this.$filterResult = true;
            this.handleFilterChange(false);
          });
        } else if ($filter.dataset.filterType === "multiselect") {
          $filter.addEventListener(
            CONFIG.events.BFV_MULTISELECT_CLICKED,
            () => {
              this.$filterResult = true;
              this.$filterChanged = true;
              this.handleFilterChange(false);
            },
          );
        } else {
          $filter.addEventListener("change", () => {
            this.$filterResult = true;
            this.handleFilterChange(false);
          });
        }

        // eslint-disable-next-line
        $filter.hasFilterListener = true;
      }
    });

    if (this.$showAllButton && !this.$showAllButton.hasFilterListener) {
      this.$showAllButton.addEventListener("click", () => {
        this.handleFilterChange(true);
      });
      this.$showAllButton.hasFilterListener = true;
    }

    if (this.$showMoreButton && !this.$showMoreButton.hasFilterListener) {
      this.$showMoreButton.addEventListener("click", () => {
        this.$filterChanged = false;
        this.$loadMore = true;
        this.handleFilterChange(false, true);
      });
      this.$showMoreButton.hasFilterListener = true;
    }

    if (this.$resetFilters && !this.$resetFilters.hasFilterListener) {
      this.$resetFilters.addEventListener("click", () => {
        this.$filterResult = false;
        this.$resetFilter = true;
        this.handleTagClick();
        this.loadContentFromApi(
          `${this.apiRoute}?${this.$resetFilters.dataset.apiTag}`,
        );
      });
      this.$resetFilters.hasFilterListener = true;
    }

    if (this.$selectedTags && this.$selectedTags.length > 0) {
      this.$selectedTags.forEach(($tag) => {
        $tag.addEventListener("click", () => {
          this.handleTagClick($tag);
        });
      });
    }

    const $loadOnClickElement = this.$root.closest(".js-lazyload-on-click");
    if (
      this.$dataContainer &&
      this.$dataContainer.dataset.apiRoute &&
      $loadOnClickElement
    ) {
      $loadOnClickElement.addEventListener(
        "click",
        this.handleLazyLoadOnClick,
        true,
      );
    }

    this.checkIfContentIsAvailable();
  }

  handleLazyLoadOnClick() {
    const $loadOnClickElement = this.$root.closest(".js-lazyload-on-click");
    $loadOnClickElement.classList.remove("js-lazyload-on-click"); // prevent nested FilteredLazyload components from creating another eventListener
    this.loadContentFromApi(this.$dataContainer.dataset.apiRoute);
    $loadOnClickElement.removeEventListener(
      "click",
      this.handleLazyLoadOnClick,
      true,
    ); // we want to load only once
  }

  setFilters() {
    this.$filters = this.$root.querySelectorAll(".js-lazyload-filter-item");
    this.$filterContainer = this.$root.querySelector(
      ".js-lazyload-filter-container",
    );
    this.$showAllButton = this.$root.querySelector(
      ".js-lazyload-filter-showall",
    );
    this.$showMoreButton = this.$root.querySelector(
      ".js-lazyload-filter-showmore",
    );
    this.$selectedTags = this.$root.querySelectorAll(".js-lazyload-filter-tag");
    this.$resetFilters = this.$root.querySelector(
      ".js-lazyload-filter-reset-button",
    );

    this.apiRoute =
      this.$filterContainer && this.$filterContainer.dataset.apiRoute;
    this.handleTagClick = this.handleTagClick.bind(this);
    this.handleLazyLoadOnClick = this.handleLazyLoadOnClick.bind(this);
  }

  /**
   * event handler for when a filter has changed
   */
  handleFilterChange(showAll, showMore, noTag) {
    this.buildTags(showAll);
    if (!noTag) {
      this.loadContentFromApi(this.buildApiUrl(showAll, showMore));
    }
  }

  handleTagClick($tag) {
    let $button;
    if ($tag) {
      $button = $tag.querySelector("button");
    }

    this.$filters.forEach(($filter) => {
      if (!$tag || $filter.dataset.filterType === $tag.dataset.filterType) {
        switch ($filter.dataset.filterType) {
          case "datepicker": {
            const inputValue = $filter.getElementsByTagName("input")[0].value;
            if (!$tag || inputValue === $button.value) {
              const event = document.createEvent("CustomEvent");
              event.initCustomEvent(
                CONFIG.events.BFV_RESET_FILTER,
                false,
                false,
                {
                  inputValue,
                },
              );

              const $datepicker = $filter.querySelector(
                '[data-module="BfvDatepicker"]',
              );
              $datepicker.dispatchEvent(event);
            }
            break;
          }

          case "multiselect": {
            const $activeMultiselectArray =
              $filter.querySelectorAll("select option");

            $activeMultiselectArray.forEach((option) => {
              if (!$tag || option.value === $button.value) {
                const { value } = option;
                const event = document.createEvent("CustomEvent");
                event.initCustomEvent(
                  CONFIG.events.BFV_RESET_FILTER,
                  false,
                  false,
                  {
                    value,
                  },
                );

                const $multiSelect = $filter.querySelector(".bfv-multiselect");
                $multiSelect.dispatchEvent(event);
              }
            });
            break;
          }

          case "text": {
            const $input = $filter.getElementsByTagName("input")[0];
            if (!$tag || $input.value === $button.value) {
              $input.value = "";
            }
            break;
          }

          default: {
            // unused in infothek so far, unclear on behavior so currently just defaults selection to first item
            const $dropdown = $filter.querySelector(".dropdown select");

            if ($dropdown) {
              $dropdown.selectedIndex = 0;
            }
          }
        }
      }
    });

    this.handleFilterChange(false, false, !$tag);
  }

  buildTags(showAll) {
    const newTagList = [];
    // build api url with all the filter pieces
    this.$filters.forEach(($filter) => {
      switch ($filter.dataset.filterType) {
        case "datepicker":
          {
            // append value for a datepicker
            const inputValue = $filter.getElementsByTagName("input")[0].value;

            if (this.$tagWrapper && inputValue !== "") {
              newTagList.push(
                this.buildTag(
                  inputValue,
                  inputValue,
                  $filter.dataset.filterType,
                ),
              );
            }
          }
          break;

        case "multiselect":
          {
            // append value for a multiselect
            const $activeMultiselectArray =
              $filter.querySelectorAll("select option");

            $activeMultiselectArray.forEach((option) => {
              if (option.selected) {
                if (this.$tagWrapper) {
                  newTagList.push(
                    this.buildTag(
                      option.text,
                      option.value,
                      $filter.dataset.filterType,
                    ),
                  );
                }
              }
            });
          }
          break;

        case "text":
          {
            const inputValue = $filter.getElementsByTagName("input")[0].value;

            if (this.$tagWrapper && inputValue !== "") {
              newTagList.push(
                this.buildTag(
                  inputValue,
                  inputValue,
                  $filter.dataset.filterType,
                ),
              );
            }
          }
          break;

        case "dropdown":
          {
            // append value for a dropdown
            const $dropdown = $filter.querySelector(".dropdown select");
            const $selected = $dropdown.options[$dropdown.selectedIndex];

            if (this.$tagWrapper) {
              newTagList.push(
                this.buildTag(
                  $selected.text,
                  $selected.value,
                  $filter.dataset.filterType,
                ),
              );
            }
          }
          break;

        default: {
          //
        }
      }
    });

    if (showAll) {
      const showAllParams = this.$showAllButton.dataset.apiTag.split("&");
      showAllParams[0] += "=1";
    }

    if (this.$tagWrapper) {
      this.$tagWrapper.innerHTML = "";

      newTagList
        .filter((x) => x)
        .forEach(($newTag) => {
          this.$tagWrapper.appendChild($newTag);
        });

      if (newTagList.length) {
        this.$tagSection.classList.add(
          "bfv-filtered-lazyload__selected-filters--has-filters",
        );
      } else {
        this.$tagSection.classList.remove(
          "bfv-filtered-lazyload__selected-filters--has-filters",
        );
      }

      this.$selectedTags = this.$root.querySelectorAll(
        ".js-lazyload-filter-tag",
      );
    }
  }

  /**
   * iterate through filters and append the selections to the apiRoute as query params
   */
  buildApiUrl(showAll, showMore) {
    const apiUrl = `${this.apiRoute.split("?")[0]}`;
    let params = (this.apiRoute.split("?")[1] || "")
      .split("&")
      .filter((x) => x); // no empty params

    // build api url with all the filter pieces
    this.$filters.forEach(($filter) => {
      switch ($filter.dataset.filterType) {
        case "datepicker":
          {
            // append value for a datepicker
            const inputValue = $filter.getElementsByTagName("input")[0].value;

            if (
              $filter.querySelector(".bfv-datepicker").dataset.type === "range"
            ) {
              // date range type append from and to
              const dateParts = inputValue.split(" bis ");
              const fromDate = dateParts[0] ? dateParts[0] : "";
              const toDate = dateParts[1] ? dateParts[1] : "";

              params.push(`fromDate=${fromDate}`);
              params.push(`toDate=${toDate}`);
            } else {
              // single date type append date
              params.push(`date=${inputValue}`);
            }
          }
          break;

        case "radio":
          {
            // append value for a radio
            const $radioGroup = $filter.querySelector(".radio-checkbox-group");
            const $checkedRadio = $radioGroup.querySelector(":checked");

            params.push(`${$filter.dataset.apiTag}=${$checkedRadio.value}`);
          }
          break;

        case "multiselect":
          {
            // append value for a multiselect
            const $activeMultiselectArray =
              $filter.querySelectorAll("select option");
            const $selectedOptions = [];

            $activeMultiselectArray.forEach((option) => {
              if (option.selected) {
                $selectedOptions.push(option.value);
              }
            });

            const $multiselectValues = $selectedOptions.join(",");
            params.push(`${$filter.dataset.apiTag}=${$multiselectValues}`);
          }
          break;

        case "text":
          {
            const inputValue = $filter.getElementsByTagName("input")[0].value;
            params.push(`${$filter.dataset.apiTag}=${inputValue}`);
          }
          break;

        default: {
          // append value for a dropdown
          const $dropdown = $filter.querySelector(".dropdown select");
          const $selected = $dropdown.options[$dropdown.selectedIndex];

          params.push(`${$filter.dataset.apiTag}=${$selected.value}`);
        }
      }
    });

    if (showAll) {
      const showAllParams = this.$showAllButton.dataset.apiTag.split("&");
      showAllParams[0] += "=1";
      params = params.concat(showAllParams);
    }

    if (showMore) {
      params.push(`from=${this.$showMoreButton.dataset.from}`);
      params.push(`size=${this.$showMoreButton.dataset.size}`);
    }

    return `${apiUrl}?${params.join("&")}`;
  }

  /**
   * call api and insert response into root
   * @param apiUrl
   */
  loadContentFromApi(apiUrl) {
    this.$contentWrapper.innerHTML = "";
    this.$noResults = "";
    this.toggleSpinner(true);

    const $dataContainer = this.$root.querySelector(
      ".js-filtered-lazyload-data-container",
    );

    window.divae.restApi
      .loadData(apiUrl, "application/xhtml+xml")
      .then((_response) => {
        /**
         * load more after filter change, further load more button
         */
        if (this.$filterResult && this.$loadMore && !this.$filterChanged) {
          const $lazyloadContentWrapper = $dataContainer.getElementsByClassName(
            "js-filtered-lazyload-content-wrapper",
          );
          const $lastElement =
            $lazyloadContentWrapper[$lazyloadContentWrapper.length - 1];
          $lastElement.innerHTML = "";
          $lastElement.innerHTML += _response;
          initModules({ rootElement: $dataContainer });
          this.checkIfContentIsAvailable();
        } else if (this.$resetFilter) {
          const $wrapper = $dataContainer;
          $wrapper.innerHTML = "";
          $wrapper.innerHTML = _response;
          initModules({ rootElement: $dataContainer });
          [this.$contentWrapper] = $wrapper.getElementsByClassName(
            "js-filtered-lazyload-content-wrapper",
          );
          this.checkIfContentIsAvailable();

          this.$resetFilter = false;

          /**
           *   filter change: filter is selected and result list has a load more button
           *   the result list will be appended
           */
        } else if (
          this.$filterResult &&
          $dataContainer &&
          _response.includes("js-filtered-lazyload-content-wrapper")
        ) {
          this.clearResultlistAndLoadNewResults($dataContainer, _response);
          this.$filterAndLoadMore = true;
          /**
           * load more after filter change: filter is selected and load more button is clicked
           * the results list will be appended
           */
        } else if (
          this.$filterResult &&
          $dataContainer &&
          this.$filterAndLoadMore
        ) {
          const [$lazyloadContentWrapper] =
            $dataContainer.getElementsByClassName(
              "js-filtered-lazyload-content-wrapper",
            );
          $lazyloadContentWrapper.innerHTML = "";
          $lazyloadContentWrapper.innerHTML += _response;
          initModules({ rootElement: $dataContainer });
          this.checkIfContentIsAvailable();

          /**
           * filter change: filter is selected and result list has no load more button
           * the result list will be replaced
           */
        } else if (this.$filterResult && $dataContainer) {
          this.clearResultlistAndLoadNewResults($dataContainer, _response);

          /**
           * result list with a load more button
           */
        } else if (_response.includes("js-filtered-lazyload-content-wrapper")) {
          const $wrapper = document.createElement("div");
          $wrapper.classList.add(
            "js-filtered-lazyload-content-wrapper",
            "bfv-filtered-lazyload__load-more-wrapper",
          );
          $wrapper.innerHTML = _response;
          const $wrapperParent = this.$contentWrapper.parentNode;
          $wrapperParent.replaceChild($wrapper, this.$contentWrapper);
          initModules({ rootElement: $wrapper });
          [this.$contentWrapper] = $wrapper.getElementsByClassName(
            "js-filtered-lazyload-content-wrapper",
          );
          this.checkIfContentIsAvailable();

          /**
           * result list without a load more button
           */
        } else {
          this.$contentWrapper.innerHTML = "";
          this.$contentWrapper.innerHTML += _response;
          initModules({ rootElement: this.$contentWrapper });
          this.checkIfContentIsAvailable();
        }

        this.toggleSpinner(false);
        this.setFilters();
        this.bindEvents();
      });
  }

  clearResultlistAndLoadNewResults($dataContainer, _response) {
    const $wrapper = $dataContainer;
    $wrapper.innerHTML = "";
    $wrapper.innerHTML = _response;
    this.$contentWrapper.innerHTML = $wrapper;
    initModules({ rootElement: $dataContainer });
    this.checkIfContentIsAvailable();
  }

  /**
   * toggle visibility of a spinner based on param
   * @param show
   */
  toggleSpinner(show) {
    if (this.$spinner) {
      this.$spinner.style.display = show ? "block" : "none";
    }
  }

  /**
   * create html element for a tag
   */
  buildTag(tagText, tagValue, tagFilterType) {
    const $tag = this.$tagSkeleton.cloneNode(true);
    $tag.style.display = "inline";
    $tag.dataset.filterType = tagFilterType;
    $tag.querySelector("button").value = tagValue;
    $tag.querySelector(".bfv-tag__text").innerHTML = tagText;

    return $tag;
  }

  hideButton() {
    if (this.$showAllButton) {
      this.$showAllButton.style.display = "none";
    } else if (this.$showMoreButton) {
      this.$showMoreButton.style.display = "none";
    }
  }

  showButton() {
    if (this.$showAllButton) {
      this.$showAllButton.style.display = "block";
    } else if (this.$showMoreButton) {
      this.$showMoreButton.style.display = "block";
    }
  }

  checkIfContentIsAvailable() {
    if (this.$noResults) {
      this.hideButton();
    } else {
      this.showButton();
    }
  }
}

export default BfvFilteredLazyload;

